import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { StatusLabel } from "../components/primitives/StatusLabel.jsx";
import { recordProductEvent } from "../features/analytics/api.js";
import {
  confirmAssistantAction,
  deleteAssistantHistory,
  fetchAssistantToday,
  reviseAssistantAction,
  sendAssistantTurn,
} from "../features/assistant/api.js";
import { useAuth } from "../features/auth/AuthProvider.jsx";
import { fetchToday } from "../features/crm/api.js";
import { AxonGuide } from "../features/onboarding/AxonGuide.jsx";
import { ConversationalOnboarding } from "../features/onboarding/ConversationalOnboarding.jsx";
import { fetchProfile } from "../features/profile/api.js";
import { useWorkspaceTimeZone } from "../features/profile/WorkspaceTimeZone.jsx";
import { AxonIcon } from "../icons/AxonIcon.jsx";
import { formatDateTime, zonedDateTimeInputToIso } from "../lib/dateTime.js";
import "./pages.css";

const PRIMARY_COMMANDS = [
  ["agenda", "Что сейчас?", "today"],
  ["leads", "Разобрать лиды", "lead"],
  ["task", "Поставить задачу", "task"],
];
const COMPOSER_STORAGE_KEY = "axon-assistant-composer-v1";
const MOBILE_ASSISTANT_QUERY = "(max-width: 699px), (max-height: 699px) and (pointer: coarse)";
const VISIBLE_MESSAGE_COUNT = 2;
const VISIBLE_LEAD_PREVIEW_COUNT = 3;

function formatDate(value, timeZone) {
  return formatDateTime(value, { timeZone, withTime: true, fallback: "срок не задан" });
}

function formatMoney(amount, currency) {
  if (amount == null || !currency) return "—";
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function makeRequestKey(scope) {
  const token = globalThis.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${scope}-${token}`;
}

export function TodayPage() {
  const auth = useAuth();
  const { timeZone: profileTimeZone } = useWorkspaceTimeZone();
  const [searchParams, setSearchParams] = useSearchParams();
  const [day, setDay] = useState(null);
  const [thread, setThread] = useState(null);
  const [onboardingProfile, setOnboardingProfile] = useState(null);
  const [status, setStatus] = useState("loading");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [online, setOnline] = useState(() => globalThis.navigator?.onLine !== false);
  const trackedViewRef = useRef(null);

  const load = useCallback(async () => {
    setStatus("loading");
    setNotice("");
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("Сессия завершилась. Войдите ещё раз.");
      const [nextDay, nextThread, nextProfile] = await Promise.all([
        fetchToday(token),
        fetchAssistantToday(token, { includeTodayQueue: false }),
        fetchProfile(token),
      ]);
      setDay(nextDay);
      setThread(nextThread);
      setOnboardingProfile(nextProfile);
      setOnline(true);
      setStatus("ready");
    } catch (error) {
      setNotice(error.message || "Не получилось собрать рабочий день.");
      setStatus("error");
    }
  }, [auth]);

  const refreshDay = useCallback(async () => {
    const token = await auth.getIdToken();
    if (!token) return;
    const nextDay = await fetchToday(token);
    setDay(nextDay);
    setOnline(true);
  }, [auth]);

  useEffect(() => {
    if (auth.status === "authenticated") void load();
  }, [auth.status, load]);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    globalThis.addEventListener?.("online", handleOnline);
    globalThis.addEventListener?.("offline", handleOffline);
    return () => {
      globalThis.removeEventListener?.("online", handleOnline);
      globalThis.removeEventListener?.("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!day?.generatedAt || trackedViewRef.current === day.generatedAt) return;
    trackedViewRef.current = day.generatedAt;
    void auth.getIdToken().then((token) => token && recordProductEvent(token, {
      name: "today.viewed",
      entityType: "today",
      entityId: day.generatedAt,
    }));
  }, [auth, day]);

  const appendTurn = useCallback((input, result, localLabel) => {
    setThread((current) => ({
      ...current,
      id: result.threadId,
      capability: result.capability,
      messages: [
        ...(current?.messages || []),
        {
          id: result.userMessageId,
          role: "user",
          content: localLabel,
          intent: null,
          blocks: [],
          createdAt: result.generatedAt,
        },
        {
          id: result.assistantMessageId,
          role: "assistant",
          content: "",
          intent: input.command || null,
          blocks: result.blocks,
          createdAt: result.generatedAt,
        },
      ],
    }));
  }, []);

  const runTurn = useCallback(async (input, localLabel) => {
    if (busy || !online) return false;
    setBusy(true);
    setNotice("");
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("Сессия завершилась. Войдите ещё раз.");
      const result = await sendAssistantTurn(token, {
        threadId: thread?.id || null,
        ...input,
      }, makeRequestKey("assistant-turn"));
      appendTurn(input, result, localLabel);
      return true;
    } catch (error) {
      setNotice(error.message || "Команда не выполнена.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [appendTurn, auth, busy, online, thread?.id]);

  const replaceDraftBlock = useCallback((draftId, nextBlock) => {
    setThread((current) => ({
      ...current,
      messages: (current?.messages || []).map((message) => ({
        ...message,
        blocks: (message.blocks || []).map((block) => (
          block.type === "action_preview" && block.draftId === draftId ? nextBlock : block
        )),
      })),
    }));
  }, []);

  const confirmAction = useCallback(async (block) => {
    if (busy || !online) return;
    setBusy(true);
    setNotice("");
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("Сессия завершилась. Войдите ещё раз.");
      const result = await confirmAssistantAction(
        token,
        block.draftId,
        makeRequestKey("assistant-confirm"),
      );
      replaceDraftBlock(block.draftId, result.block);
      await refreshDay();
    } catch (error) {
      if (error.preview) replaceDraftBlock(block.draftId, error.preview);
      setNotice(error.message || "Действие не подтверждено.");
    } finally {
      setBusy(false);
    }
  }, [auth, busy, online, refreshDay, replaceDraftBlock]);

  const reviseAction = useCallback(async (block, changes) => {
    if (busy || !online) return;
    setBusy(true);
    setNotice("");
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("Сессия завершилась. Войдите ещё раз.");
      const result = await reviseAssistantAction(
        token,
        block.draftId,
        changes,
        makeRequestKey("assistant-revise"),
      );
      replaceDraftBlock(block.draftId, result.block);
    } catch (error) {
      setNotice(error.message || "Не получилось изменить предпросмотр.");
    } finally {
      setBusy(false);
    }
  }, [auth, busy, online, replaceDraftBlock]);

  const clearHistory = useCallback(async () => {
    if (busy || !online) return false;
    setBusy(true);
    try {
      const token = await auth.getIdToken();
      if (!token) throw new Error("Сессия завершилась. Войдите ещё раз.");
      await deleteAssistantHistory(token);
      const fresh = await fetchAssistantToday(token, { includeTodayQueue: false });
      setThread(fresh);
      setNotice("");
      return true;
    } catch (error) {
      setNotice(error.message || "История не удалена.");
      return false;
    } finally {
      setBusy(false);
    }
  }, [auth, busy, online]);

  const queue = Array.isArray(day?.queue) ? day.queue : thread?.todayQueue || [];
  const timeZone = thread?.timezone || profileTimeZone;
  const requestedTaskId = searchParams.get("task");
  const requestedTask = requestedTaskId
    ? queue.find((item) => item.kind === "personal_task" && item.entityId === requestedTaskId)
    : null;
  const dismissTask = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete("task");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  return (
    <div className="today-page today-command-page">
      {status === "loading" && !day ? <TodayStatus label="Собираем рабочий день" /> : null}
      {status === "error" && !day ? (
        <TodayStatus label={notice} error onRetry={() => void load()} />
      ) : null}

      {day && thread ? (
        <>
          {!online ? (
            <p className="command-notice command-notice--offline" role="status">
              Нет сети. Снимок можно читать, действия временно отключены.
            </p>
          ) : null}
          {notice ? <p className="command-notice" role="alert">{notice}</p> : null}

          <main className="command-desk">
            <AssistantSurface
              thread={thread}
              busy={busy}
              online={online}
              onTurn={runTurn}
              onConfirm={confirmAction}
              onRevise={reviseAction}
              onClear={clearHistory}
              auth={auth}
              timeZone={timeZone}
              onboardingProfile={onboardingProfile}
              onOnboardingActivated={refreshDay}
              hasQueue={Boolean(queue.length)}
              leadFocusState={thread.leadFocusState || null}
              requestedTask={requestedTask}
              onDismissTask={dismissTask}
            />
          </main>
        </>
      ) : null}
    </div>
  );
}

function CommandChips({ busy, online, onRun }) {
  return (
    <nav className="command-chips" aria-label="Быстрые команды">
      {PRIMARY_COMMANDS.map(([command, label, icon]) => (
        <button
          key={command}
          type="button"
          disabled={busy || !online}
          onClick={() => void onRun({ command }, label)}
        >
          <AxonIcon name={icon} size={18} active={command === "agenda"} />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
}

function AssistantSurface({
  thread,
  busy,
  online,
  onTurn,
  onConfirm,
  onRevise,
  onClear,
  auth,
  timeZone,
  onboardingProfile,
  onOnboardingActivated,
  hasQueue,
  leadFocusState,
  requestedTask,
  onDismissTask,
}) {
  const [draft, setDraft] = useState(() => {
    try {
      return globalThis.sessionStorage?.getItem(COMPOSER_STORAGE_KEY) || "";
    } catch {
      return "";
    }
  });
  const [confirmClear, setConfirmClear] = useState(false);
  const [showEarlierMessages, setShowEarlierMessages] = useState(false);
  const messagesRef = useRef(null);
  const messagesTailRef = useRef(null);
  const previousMessageCountRef = useRef(thread.messages?.length || 0);
  const initialMobilePositionedRef = useRef(false);

  useEffect(() => {
    const nextCount = thread.messages?.length || 0;
    const previousCount = previousMessageCountRef.current;
    previousMessageCountRef.current = nextCount;
    const messages = messagesRef.current;
    if (!messages || !nextCount) return;
    if (globalThis.matchMedia?.(MOBILE_ASSISTANT_QUERY)?.matches === true) {
      const shouldPositionLatest = !initialMobilePositionedRef.current || nextCount > previousCount;
      initialMobilePositionedRef.current = true;
      if (shouldPositionLatest) {
        const positionLatest = () => {
          messagesTailRef.current?.scrollIntoView?.({ block: "end", inline: "nearest" });
        };
        if (globalThis.requestAnimationFrame) globalThis.requestAnimationFrame(positionLatest);
        else positionLatest();
      }
      return;
    }
    messages.scrollTop = messages.scrollHeight;
  }, [thread.messages?.length]);

  useEffect(() => {
    const positionLatest = () => {
      if (globalThis.matchMedia?.(MOBILE_ASSISTANT_QUERY)?.matches !== true) return;
      const scrollLatest = () => {
        messagesTailRef.current?.scrollIntoView?.({ block: "end", inline: "nearest" });
      };
      if (globalThis.requestAnimationFrame) {
        globalThis.requestAnimationFrame(() => globalThis.requestAnimationFrame(scrollLatest));
      } else {
        scrollLatest();
      }
    };
    globalThis.addEventListener?.("resize", positionLatest);
    globalThis.visualViewport?.addEventListener?.("resize", positionLatest);
    return () => {
      globalThis.removeEventListener?.("resize", positionLatest);
      globalThis.visualViewport?.removeEventListener?.("resize", positionLatest);
    };
  }, []);

  useEffect(() => {
    try {
      if (draft) globalThis.sessionStorage?.setItem(COMPOSER_STORAGE_KEY, draft);
      else globalThis.sessionStorage?.removeItem(COMPOSER_STORAGE_KEY);
    } catch {
      // В защищённых и приватных браузерных режимах sessionStorage может быть отключён.
    }
  }, [draft]);

  const submit = async (event) => {
    event.preventDefault();
    const normalized = draft.trim().replace(/\s+/g, " ");
    if (!normalized) return;
    const sent = await onTurn({ message: normalized }, normalized);
    if (sent) setDraft("");
  };

  const clear = async () => {
    const cleared = await onClear();
    if (cleared) setConfirmClear(false);
  };

  const messages = thread.messages || [];
  const hiddenMessageCount = Math.max(0, messages.length - VISIBLE_MESSAGE_COUNT);
  const visibleMessages = showEarlierMessages
    ? messages
    : messages.slice(-VISIBLE_MESSAGE_COUNT);

  return (
    <section className="assistant-surface" aria-labelledby="assistant-title">
      <header className="assistant-surface__head">
        <h1 id="assistant-title" className="type-section">Рабочий диалог</h1>
        {thread.messages?.length ? (
          <button type="button" className="text-button" onClick={() => setConfirmClear(true)}>
            Очистить
          </button>
        ) : null}
      </header>

      {requestedTask ? (
        <FocusedPersonalTask
          item={requestedTask}
          onDismiss={onDismissTask}
          timeZone={timeZone}
        />
      ) : null}

      {confirmClear ? (
        <div className="history-confirm" role="dialog" aria-label="Удалить историю диалога">
          <p>Удалить видимую историю? Задачи, сделки и деньги останутся.</p>
          <div>
            <button type="button" onClick={() => void clear()} disabled={busy}>Удалить</button>
            <button type="button" onClick={() => setConfirmClear(false)}>Отмена</button>
          </div>
        </div>
      ) : null}

      <div ref={messagesRef} className="assistant-messages" aria-live="polite" aria-busy={busy}>
        {onboardingProfile && !onboardingProfile.onboardingComplete ? (
          <ConversationalOnboarding
            initialProfile={onboardingProfile}
            onActivated={onOnboardingActivated}
          />
        ) : null}
        {!thread.messages?.length && (!onboardingProfile || onboardingProfile.onboardingComplete) ? (
          <AssistantWelcome hasQueue={hasQueue} leadFocusState={leadFocusState} />
        ) : null}
        {hiddenMessageCount ? (
          <button
            type="button"
            className="assistant-history-toggle"
            aria-expanded={showEarlierMessages}
            onClick={() => setShowEarlierMessages((current) => !current)}
          >
            {showEarlierMessages
              ? "Скрыть предыдущие"
              : `Показать предыдущие ${hiddenMessageCount}`}
          </button>
        ) : null}
        {visibleMessages.map((message) => (
          <article
            className={`assistant-message assistant-message--${message.role}`}
            key={message.id}
          >
            <div className="assistant-message__author type-meta">
              {message.role === "user" ? "Вы" : "Axon"}
            </div>
            {message.role === "user" ? <p>{message.content}</p> : (
              <div className="assistant-blocks">
                {(message.blocks || []).map((block, index) => (
                  <AssistantBlock
                    key={`${message.id}-${block.type}-${block.draftId || block.promptId || index}`}
                    block={block}
                    busy={busy}
                    online={online}
                    onSelect={(option) => onTurn(
                      { selection: { promptId: block.promptId, optionId: option.optionId } },
                      option.label,
                    )}
                    onConfirm={onConfirm}
                    onRevise={onRevise}
                    auth={auth}
                    timeZone={timeZone}
                  />
                ))}
              </div>
            )}
          </article>
        ))}
        {busy ? <div className="assistant-thinking type-meta">Проверяю факты…</div> : null}
        <div ref={messagesTailRef} className="assistant-messages__tail" aria-hidden="true" />
      </div>

      {onboardingProfile?.onboardingComplete ? (
        <>
          <CommandChips busy={busy} online={online} onRun={onTurn} />
          <form className="assistant-composer" onSubmit={submit}>
            <label htmlFor="assistant-message">Сообщение ассистенту</label>
            <textarea
              id="assistant-message"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Например: вернуться к Анне завтра"
              rows="2"
              maxLength="4000"
              disabled={!online}
            />
            <button
              type="submit"
              aria-label="Отправить сообщение"
              disabled={busy || !online || !draft.trim()}
            >
              <AxonIcon name="send" size={20} active />
            </button>
          </form>
        </>
      ) : null}
    </section>
  );
}

function FocusedPersonalTask({ item, onDismiss, timeZone }) {
  return (
    <section className="assistant-task-focus" aria-label="Открытая задача">
      <div>
        <span className="type-meta">Личная задача</span>
        <h2>{item.title}</h2>
        <p>{item.dueAt ? `Срок: ${formatDate(item.dueAt, timeZone)}` : "Без срока"}</p>
      </div>
      <button type="button" className="text-button" onClick={onDismiss}>Закрыть</button>
    </section>
  );
}

function AssistantWelcome({ hasQueue, leadFocusState }) {
  const hasWork = hasQueue || Boolean(leadFocusState);
  const message = hasQueue
    ? "Есть дела на сегодня — с чего начнём?"
    : leadFocusState === "new"
    ? "Есть подходящие заказы — начните с команды «Разобрать лиды»."
    : leadFocusState === "review"
    ? "Есть заказы, где нужен ваш выбор — откройте «Разобрать лиды»."
    : "На сегодня спокойно — можно поставить задачу или спросить о работе.";
  return (
    <div className="assistant-welcome">
      <AxonGuide state={hasWork ? "notice" : "welcome"} />
      <p>{message}</p>
    </div>
  );
}

function AssistantBlock({ block, busy, online, onSelect, onConfirm, onRevise, auth, timeZone }) {
  if (block.type === "text") return <p className="assistant-text">{block.text}</p>;
  if (block.type === "agenda") {
    return (
      <section className="assistant-card">
        <h3>{block.title}</h3>
        <p>{block.summary}</p>
        <CompactQueue items={block.items} />
      </section>
    );
  }
  if (block.type === "entity_options") {
    return (
      <section className="assistant-card">
        <h3>{block.title}</h3>
        <div className="entity-options">
          {block.options.map((option) => (
            <button
              type="button"
              key={option.optionId}
              onClick={() => void onSelect(option)}
              disabled={busy || !online}
            >
              <strong>{option.label}</strong>
              <span>{option.context}</span>
            </button>
          ))}
        </div>
      </section>
    );
  }
  if (block.type === "lead_list") {
    const leadItems = Array.isArray(block.items) ? block.items : [];
    const visibleLeadItems = leadItems.slice(0, VISIBLE_LEAD_PREVIEW_COUNT);
    return (
      <section className="assistant-card">
        <h3>{block.title}</h3>
        <p>{block.summary}</p>
        <ul className="assistant-list">
          {visibleLeadItems.map((item) => (
            <li key={item.assignmentId}>
              <div><strong>{item.title}</strong><span>{item.sourceName}</span></div>
              <Link to={item.href}>Открыть</Link>
            </li>
          ))}
        </ul>
        <Link className="assistant-card__more" to="/leads">
          {leadItems.length > visibleLeadItems.length ? "Открыть все лиды" : "Перейти в лиды"}
        </Link>
      </section>
    );
  }
  if (block.type === "money_summary") {
    return (
      <section className="assistant-card">
        <h3>{block.title}</h3>
        <p>{block.summary}</p>
        <ul className="assistant-list">
          {block.items.map((item) => (
            <li key={item.invoiceId}>
              <div>
                <strong>{item.number} · {formatMoney(item.balanceAmount, item.currency)}</strong>
                <span>{item.projectTitle} · {formatDate(item.dueOn, timeZone)}</span>
              </div>
              <Link to={item.href}>Счёт</Link>
            </li>
          ))}
        </ul>
      </section>
    );
  }
  if (block.type === "source_focus") {
    return (
      <section className="assistant-card assistant-card--focus">
        <h3>{block.title}</h3>
        <p>{block.body}</p>
        <Link to={block.href}>{block.actionLabel}</Link>
      </section>
    );
  }
  if (block.type === "reply_draft") {
    return <ReplyDraft block={block} auth={auth} />;
  }
  if (block.type === "action_preview") {
    return (
      <ActionPreview
        block={block}
        busy={busy}
        online={online}
        onConfirm={onConfirm}
        onRevise={onRevise}
        timeZone={timeZone}
      />
    );
  }
  if (block.type === "action_result") {
    return (
      <section className="assistant-card action-result" role="status">
        <AxonIcon name="check" size={24} active />
        <div><h3>{block.title}</h3><p>Изменение сохранено и появится в рабочей очереди.</p></div>
        {block.href ? <Link to={block.href}>Открыть</Link> : null}
      </section>
    );
  }
  if (block.type === "degraded_notice") {
    return (
      <section className="assistant-card degraded-notice" role="status">
        <AxonIcon name="warning" size={22} />
        <div><h3>{block.title}</h3><p>{block.body}</p></div>
      </section>
    );
  }
  return <p className="degraded-notice">Этот безопасный блок пока не поддерживается интерфейсом.</p>;
}

function CompactQueue({ items = [] }) {
  if (!items.length) return null;
  return (
    <ol className="compact-queue">
      {items.map((item, index) => (
        <li key={item.id}>
          <span className="type-meta">{String(index + 1).padStart(2, "0")}</span>
          <div><strong>{item.title}</strong><small>{item.context}</small></div>
          <Link to={item.primaryAction.href}>{item.primaryAction.label}</Link>
        </li>
      ))}
    </ol>
  );
}

function ActionPreview({ block, busy, online, onConfirm, onRevise, timeZone }) {
  const [editing, setEditing] = useState(false);
  const titleField = block.fields.find((field) => field.key === "title");
  const [title, setTitle] = useState(titleField?.value || "");
  const [dueAt, setDueAt] = useState("");

  useEffect(() => {
    setTitle(titleField?.value || "");
    setDueAt("");
    setEditing(false);
  }, [block.draftId, titleField?.value]);

  const submitRevision = (event) => {
    event.preventDefault();
    const changes = {};
    if (title.trim() !== titleField?.value) changes.title = title.trim();
    if (dueAt) changes.dueAt = zonedDateTimeInputToIso(dueAt, timeZone);
    if (Object.keys(changes).length) void onRevise(block, changes);
    setEditing(false);
  };

  return (
    <section className="assistant-card action-preview">
      <div className="action-preview__head">
        <StatusLabel tone="warning">Нужно подтверждение</StatusLabel>
        <span className="type-meta">15 минут</span>
      </div>
      <h3>{block.title}</h3>
      <p className="action-preview__target">{block.targetLabel}</p>
      <dl>
        {block.fields.map((field) => (
          <div key={field.key}><dt>{field.label}</dt><dd>{field.value}</dd></div>
        ))}
      </dl>
      {editing ? (
        <form className="action-revision" onSubmit={submitRevision}>
          <label>Формулировка<input value={title} onChange={(event) => setTitle(event.target.value)} /></label>
          <label>Новый срок<input type="datetime-local" value={dueAt} onChange={(event) => setDueAt(event.target.value)} /></label>
          <div><button type="submit">Сохранить изменения</button><button type="button" onClick={() => setEditing(false)}>Отмена</button></div>
        </form>
      ) : (
        <div className="action-preview__actions">
          <button type="button" onClick={() => void onConfirm(block)} disabled={busy || !online}>
            Подтвердить
          </button>
          <button type="button" onClick={() => setEditing(true)} disabled={busy || !online}>
            Изменить
          </button>
        </div>
      )}
    </section>
  );
}

function ReplyDraft({ block, auth }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await globalThis.navigator?.clipboard?.writeText(block.draft);
    setCopied(true);
    const token = await auth.getIdToken();
    if (token) void recordProductEvent(token, {
      name: "reply_draft.copied",
      entityType: "reply_draft_generation",
      entityId: block.generationId,
    });
  };
  return (
    <section className="assistant-card reply-draft">
      <div className="reply-draft__head"><h3>Черновик ответа</h3><span className="type-meta">Не отправлен</span></div>
      <p className="reply-draft__copy">{block.draft}</p>
      <details><summary>Факты ответа</summary><ul>{block.facts.map((fact) => <li key={fact}>{fact}</li>)}</ul></details>
      <div>
        <button type="button" onClick={() => void copy()}>{copied ? "Скопировано" : "Копировать"}</button>
        <Link to={`/leads?lead=${block.assignmentId}`}>Открыть лид</Link>
      </div>
    </section>
  );
}

function TodayStatus({ label, error = false, onRetry }) {
  return (
    <section className={`today-status${error ? " is-error" : ""}`} aria-live="polite">
      <AxonIcon name={error ? "warning" : "today"} size={34} active={!error} />
      <p>{label}</p>
      {onRetry ? <button type="button" onClick={onRetry}>Повторить</button> : null}
    </section>
  );
}
